import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import {
  transformAssociateDocumentUploadError,
  type AssociateDocumentUploadError,
} from "helpers/documentUploadApiHelpers";
import { formatCL } from "helpers/formatCL";

import type {
  Account,
  AssociateCompanyDocs,
  CLDetails,
  DuplicateLeadNameCheck,
  RegisterLead,
  RegisterLeadPartner,
  RegisterLeadPartnerCreditLimit,
  SalesCodesResponse,
  SalesLead,
} from "types";
import { accountrtk } from "./api-accounts";
import { comments as commentsAPI } from "./api-comments";
import { companiesrtk } from "./api-company";
import type {
  CLOpportunityData,
  CofaceOptions,
  Timeline,
} from "./api-origination.d";

const formatReps = (reps: any[]) =>
  reps?.map((item) => ({
    ...item,
    name: `${item.first_name} ${item.last_name}`,
  }));

export const origination = createApi({
  reducerPath: "origination",
  tagTypes: ["STATUS", "CREDIT_LIMITS", "CREDIT_LIMITS_REPORTING"],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/origination`,
  }),
  endpoints: (builder) => ({
    getOnboardingStatuses: builder.query<any, { onbordpipeparams: any }>({
      query: ({ onbordpipeparams }) => ({
        url: `/onboardings${onbordpipeparams}`,
        method: "get",
      }),

      transformResponse: (response) => response.data,
      providesTags: ["STATUS"],
    }),

    getCreditLimits: builder.query<any, { creditlimitsparams: any }>({
      query: ({ creditlimitsparams }) => ({
        url: `/credit-limits${creditlimitsparams}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["CREDIT_LIMITS"],
    }),

    getInsurerIdentification: builder.query<CofaceOptions[], string>({
      query: (cl_id) => ({
        url: `/credit-limits/${cl_id}/insurer_identification`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getCreditLimit: builder.query<CLDetails, string>({
      query: (clr) => ({ url: `/credit-limits/${clr}`, method: "get" }),
      transformResponse: (response) => formatCL(response?.data),
      providesTags: ["CREDIT_LIMITS"],
    }),
    getSalesCodes: builder.query<SalesCodesResponse, null>({
      query: () => ({ url: `/codes`, method: "get" }),
      transformResponse: (response) => response.data,
    }),
    getSalesList: builder.query<SalesLead[], null>({
      query: () => ({ url: `/list-sales`, method: "get" }),
      transformResponse: (response) => formatReps(response?.data),
    }),
    getSalesPartnersList: builder.query<SalesLead[], null>({
      query: () => ({ url: `/list-sales-partners`, method: "get" }),
      transformResponse: (response) => formatReps(response?.data),
    }),
    contactCreditLimitCreator: builder.mutation<
      string,
      {
        credit_limit_id: string;
        comments: {
          comments: string;
        };
      }
    >({
      query({ credit_limit_id, comments }) {
        return {
          url: `/credit-limits/${credit_limit_id}/contact_creator`,
          method: "POST",
          data: { ...comments },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    requestCreditLimit: builder.mutation<
      string,
      { credit_limit_id: string; coface_id: string; comments: string }
    >({
      query({ credit_limit_id, coface_id, comments }) {
        return {
          url: `/credit-limits/${credit_limit_id}/request_indication`,
          method: "POST",
          data: { comments, coface_id },
          // In backend, the schema is:
          //   request_credit_limit_schema = {
          //     "type": "object",
          //     "properties": {"coface_id": {"type": "string"}, "comments": {"type": ["string", "null"]}},
          //     "required": ["coface_id"],
          // }
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    postCofaceManualInput: builder.mutation<
      string,
      {
        credit_limit_id: string;
        rating: number;
        indication: number;
        description: string;
      }
    >({
      query({ credit_limit_id, rating, indication, description }) {
        return {
          url: `/credit-limits/${credit_limit_id}/insurer-manual`,
          method: "POST",
          data: { rating, indication, description },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    rejectCreditLimit: builder.mutation<
      string,
      { credit_limit_id: string; comments: string }
    >({
      query({ credit_limit_id, comments }) {
        return {
          url: `/credit-limits/${credit_limit_id}/reject`,
          method: "POST",
          data: { comments },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),

    getOnboardingStatusData: builder.query<any, void>({
      query: () => ({
        url: `/onboardings`,
        method: "GET",
        params: {
          pagination: false,
        },
      }),
      transformResponse: (response) => response.data?.onboarding_details,
      providesTags: ["STATUS"],
    }),

    resubmitCreditLimit: builder.mutation<
      string,
      {
        credit_limit_id: string;
        comments: {
          comments: string;
        };
      }
    >({
      query({ credit_limit_id, comments }) {
        return {
          url: `/credit-limits/${credit_limit_id}/resubmit`,
          method: "POST",
          data: { ...comments },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    editCreditLimit: builder.mutation<
      CLDetails,
      {
        id: string;
        status?: string;
        comment?: string;
        sales_lead?: string;
        credit_limit_request_internal_amount?: number;
        currency?: string;
      }
    >({
      query(payload) {
        const {
          id,
          status,
          comment,
          sales_lead,
          credit_limit_request_internal_amount,
          currency,
        } = payload;

        return {
          url: `/credit-limits/${id}`,
          method: "PUT",
          data: {
            status,
            comment,
            sales_lead,
            credit_limit_request_internal_amount,
            currency,
          },
        };
      },
      transformResponse: (response) => formatCL(response?.data),
      invalidatesTags: ["CREDIT_LIMITS"],
    }),
    editPartnerCreditLimit: builder.mutation<
      CLDetails,
      {
        id: string;
        sales_lead?: string;
        credit_limit_request_internal_amount?: number;
      }
    >({
      query(payload) {
        const { id, sales_lead, credit_limit_request_internal_amount } =
          payload;

        return {
          url: `/credit-limits/partner/${id}`,
          method: "PUT",
          data: {
            sales_lead,
            credit_limit_request_internal_amount,
          },
        };
      },
      transformResponse: (response) => formatCL(response?.data),
      invalidatesTags: ["CREDIT_LIMITS"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountrtk.util.invalidateTags([
              "ACCOUNT",
              "Partners",
              "ACCOUNT_CREDIT_LIMIT",
              "LEAD",
              "TERMSHEETS",
            ]),
          );
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    requestedAmountCreditLimit: builder.mutation<
      { updated_partners: string[] },
      {
        main_credit_limit_id: string;
        main_credit_limit_amount: number;
        currency?: string;
        partner_allocations: {
          partner_id: string;
          allocated_amount: number;
        }[];
      }
    >({
      query: (payload) => ({
        url: `/credit-limits/requested_amount`,
        method: "PUT",
        data: payload,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["CREDIT_LIMITS"],
    }),

    approveCreditLimit: builder.mutation<
      string,
      { credit_limit_id: string; comments: { comments: string } }
    >({
      query({ credit_limit_id, comments }) {
        return {
          url: `/credit-limits/${credit_limit_id}/approval`,
          method: "POST",
          data: { ...comments },
        };
      },
      invalidatesTags: () => ["CREDIT_LIMITS"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(commentsAPI.util.invalidateTags(["CREDIT_LIMIT_COMMENTS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    associateDocsInternal: builder.mutation<
      any,
      { id: string; data: AssociateCompanyDocs }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_category", data.document_category);
        formData.append("document_type", data.document_type);
        //eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (data.year) formData.append("year", data.year);
        return {
          url: `/accounts/${id}/documents`,
          method: "POST",
          data: formData,
        };
      },
      transformErrorResponse: (error, _meta, { data }) =>
        transformAssociateDocumentUploadError(
          error as AssociateDocumentUploadError,
          data.document_type,
        ),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(companiesrtk.util.invalidateTags(["Company"]));
          dispatch(
            accountrtk.util.invalidateTags([
              "ACCOUNT_DOCS",
              "Company",
              "ACCOUNT_ONBOARDING_STATUS",
            ]),
          );
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),

    checkLeadName: builder.query<DuplicateLeadNameCheck, string>({
      query: (name) => ({
        url: `/lead/name-check`,
        method: "get",
        params: { name },
      }),
      transformResponse: (response) => response?.data,
    }),
    declineDuplicateLead: builder.mutation<
      DuplicateLeadNameCheck,
      { name: string }
    >({
      query: (data) => ({
        url: `/lead/duplicate-declined`,
        method: "POST",
        data,
      }),
      transformResponse: (response) => response?.data,
    }),
    createLead: builder.mutation<Account, RegisterLead>({
      query: (data) => {
        return {
          url: `/lead`,
          method: "POST",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(accountrtk.util.invalidateTags(["ACCOUNTS", "Company"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    createLeadPartner: builder.mutation<Account, RegisterLeadPartner>({
      query: ({ lead_id, ...payload }) => {
        return {
          url: `/lead/${lead_id}/partners`,
          method: "POST",
          data: payload,
        };
      },
      transformResponse: (response) => response?.data,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountrtk.util.invalidateTags(["ACCOUNTS", "Company", "LEAD"]),
          );
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    createLeadPartnerCreditLimit: builder.mutation<
      Account,
      RegisterLeadPartnerCreditLimit
    >({
      query: ({ lead_id, ...payload }) => {
        return {
          url: `/lead/${lead_id}/credit-limit`,
          method: "POST",
          data: payload,
        };
      },
      transformResponse: (response) => response?.data,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountrtk.util.invalidateTags(["ACCOUNTS", "Company", "LEAD"]),
          );
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    getCreditLimitsOpportunities: builder.query<CLOpportunityData, null>({
      query: () => ({ url: "/credit-limits-reporting", method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["CREDIT_LIMITS_REPORTING"],
    }),
    getCreditLimitTimeline: builder.query<Timeline[], string>({
      query: (id) => ({ url: `/credit-limits/${id}/timeline`, method: "get" }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetOnboardingStatusesQuery,
  useGetOnboardingStatusDataQuery,
  useGetCreditLimitsQuery,
  useGetInsurerIdentificationQuery,
  useContactCreditLimitCreatorMutation,
  usePostCofaceManualInputMutation,
  useRequestCreditLimitMutation,
  useRejectCreditLimitMutation,
  useResubmitCreditLimitMutation,
  useApproveCreditLimitMutation,
  useAssociateDocsInternalMutation,
  useGetCreditLimitQuery,
  useEditCreditLimitMutation,
  useEditPartnerCreditLimitMutation,
  useRequestedAmountCreditLimitMutation,
  useGetSalesListQuery,
  useGetSalesPartnersListQuery,
  useLazyCheckLeadNameQuery,
  useDeclineDuplicateLeadMutation,
  useCreateLeadMutation,
  useCreateLeadPartnerMutation,
  useCreateLeadPartnerCreditLimitMutation,
  useGetCreditLimitsOpportunitiesQuery,
  useGetCreditLimitTimelineQuery,
  useGetSalesCodesQuery,
} = origination;
